/**
 * Canvas Studio — tools, palettes, presets and hard limits.
 * Literal hex values are used everywhere Fabric touches the canvas (Fabric
 * needs real colors, not CSS var() references).
 */
import {
  MousePointer2, Hand, Square, Circle, Triangle, Minus, ArrowUpRight,
  Star, Hexagon, Pen, Brush, Eraser, Type, StickyNote, Image as ImageIcon,
  Pill, RectangleHorizontal, Diamond, Frame,
} from 'lucide-react';

// ── Tools (MVP set — flowchart elements deferred) ──────────────────────────────
export const TOOL_GROUPS = [
  {
    key: 'select',
    tools: [
      { id: 'select', label: 'Select', Icon: MousePointer2, shortcut: 'V' },
      { id: 'hand', label: 'Pan', Icon: Hand, shortcut: 'H' },
    ],
  },
  {
    key: 'shapes',
    label: 'Shapes',
    tools: [
      { id: 'rectangle', label: 'Rectangle', Icon: Square, shortcut: 'R' },
      { id: 'circle', label: 'Ellipse', Icon: Circle, shortcut: 'C' },
      { id: 'triangle', label: 'Triangle', Icon: Triangle },
      { id: 'line', label: 'Line', Icon: Minus, shortcut: 'L' },
      { id: 'arrow', label: 'Arrow', Icon: ArrowUpRight, shortcut: 'A' },
      { id: 'star', label: 'Star', Icon: Star, shortcut: 'S' },
      { id: 'polygon', label: 'Polygon', Icon: Hexagon },
    ],
  },
  {
    key: 'draw',
    label: 'Drawing',
    tools: [
      { id: 'pen', label: 'Pen', Icon: Pen },
      { id: 'brush', label: 'Brush', Icon: Brush, shortcut: 'B' },
      { id: 'eraser', label: 'Eraser', Icon: Eraser, shortcut: 'X' },
    ],
  },
  {
    key: 'objects',
    label: 'Objects',
    tools: [
      { id: 'text', label: 'Text', Icon: Type, shortcut: 'T' },
      { id: 'sticky', label: 'Sticky note', Icon: StickyNote, shortcut: 'N' },
      { id: 'image', label: 'Image', Icon: ImageIcon },
      { id: 'frame', label: 'Frame', Icon: Frame, shortcut: 'F' },
    ],
  },
  {
    key: 'flowchart',
    label: 'Flowchart',
    tools: [
      { id: 'flowchart_start', label: 'Start / End', Icon: Pill },
      { id: 'flowchart_process', label: 'Process', Icon: RectangleHorizontal },
      { id: 'flowchart_decision', label: 'Decision', Icon: Diamond },
    ],
  },
];

// Starter templates inserted at the viewport centre (see CanvasCore.insertTemplate).
export const TEMPLATES = [
  { id: 'flowchart', label: 'Flowchart', desc: 'Start → process → decision with two branches' },
  { id: 'kanban', label: 'Kanban board', desc: 'To Do · In Progress · Done columns with cards' },
  { id: 'mindmap', label: 'Mind map', desc: 'A central idea with four radiating branches' },
  { id: 'retro', label: 'Retro board', desc: 'Start · Stop · Continue columns for a sprint retro' },
  { id: 'swot', label: 'SWOT', desc: 'Strengths · Weaknesses · Opportunities · Threats grid' },
  { id: 'timeline', label: 'Timeline', desc: 'A baseline with alternating milestone markers' },
  { id: 'orgchart', label: 'Org chart', desc: 'A leader with three direct reports' },
  { id: 'journey', label: 'User journey', desc: 'Five stages with an actions row' },
];

export const ALL_TOOLS = TOOL_GROUPS.flatMap((g) => g.tools);

// key → tool id (single-letter shortcuts)
export const SHORTCUT_MAP = ALL_TOOLS.reduce((m, t) => {
  if (t.shortcut) m[t.shortcut.toLowerCase()] = t.id;
  return m;
}, {});

// ── Sticky-note palette (Tailwind *-100/200 tints) ─────────────────────────────
export const STICKY_COLORS = [
  { name: 'Amber', bg: '#fde68a', fg: '#78350f' },
  { name: 'Blue', bg: '#bfdbfe', fg: '#1e3a8a' },
  { name: 'Emerald', bg: '#a7f3d0', fg: '#064e3b' },
  { name: 'Pink', bg: '#fbcfe8', fg: '#831843' },
  { name: 'Violet', bg: '#ddd6fe', fg: '#4c1d95' },
  { name: 'Red', bg: '#fecaca', fg: '#7f1d1d' },
  { name: 'Sky', bg: '#bae6fd', fg: '#0c4a6e' },
  { name: 'Purple', bg: '#e9d5ff', fg: '#581c87' },
];

// ── Canvas paper backgrounds ───────────────────────────────────────────────────
// `pattern` drives a CSS overlay class (dots/grid) synced to pan+zoom.
export const BACKGROUNDS = {
  light: { label: 'Light', color: '#f8fafc', pattern: null },
  dark: { label: 'Dark', color: '#0f172a', pattern: null },
  dots: { label: 'Dots', color: '#fafafa', pattern: 'dots' },
  grid: { label: 'Grid', color: '#fafafa', pattern: 'grid' },
  transparent: { label: 'Transparent', color: '', pattern: null },
};

// ── Shape style defaults (clean wireframe look on a light canvas) ──────────────
export const SHAPE_DEFAULTS = {
  fill: '#ffffff',
  stroke: '#0f172a',
  strokeWidth: 2,
};
export const TEXT_DEFAULTS = {
  fill: '#0f172a',
  fontSize: 24,
  fontFamily: "'Inter', sans-serif",
};

export const FONT_FAMILIES = [
  "'Inter', sans-serif",
  'Arial, sans-serif',
  'Georgia, serif',
  "'Times New Roman', serif",
  "'Courier New', monospace",
  'Comic Sans MS, cursive',
];

// ── Limits / behaviour (from the spec) ─────────────────────────────────────────
export const MAX_OBJECTS = 2000;
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
export const MAX_HISTORY = 50;
export const AUTOSAVE_INTERVAL = 30_000; // 30 s
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 5.0;
export const ZOOM_PRESETS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3];
export const GRID_PRESETS = [20, 40, 80];

// Custom Fabric props we serialise so object identity / lock / erasability
// survive round-trips.
export const EXTRA_PROPS = ['id', 'customType', 'locked', 'stickyFg', 'erasable'];

export const uid = () =>
  `o_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
